import { useEffect, useMemo, useState, useRef } from 'react';
import { apiBaseUrl } from '../../config';
import { HttpTransportType, HubConnectionBuilder, JsonHubProtocol, LogLevel } from '@microsoft/signalr';

const UnitBoxChartSignalR = props => {

    const {
        token,
        companyId,
        requestBody,
        lastProcessedTimestamp,
        onIncomingData,
        onReconnect,
    } = props;

    const [connection, setConnection] = useState(null);
    const isListenerAttached = useRef(false);
    const previousConnectionState = useRef(null);

    const signalRConnection = useMemo(
        () => {
            const buildHubUrl = companyId
                ? `${apiBaseUrl}/ws/data?companyid=${companyId}`
                : `${apiBaseUrl}/ws/data`;

            const protocol = new JsonHubProtocol();
            const transport = HttpTransportType.WebSockets;

            const options = {
                skipNegotiation: true,
                transport,
                accessTokenFactory: () => token,
                headers: { authorization: token ? `Token ${token}` : `` },
            };

            return new HubConnectionBuilder()
                .withUrl(buildHubUrl, options)
                .withAutomaticReconnect()
                .withHubProtocol(protocol)
                .configureLogging(LogLevel.Information)
                .build();
        }, [token, companyId],
    );

   
    useEffect(() => {
        if (signalRConnection) {
            signalRConnection.start().then(() => {
                setConnection(signalRConnection);
                isListenerAttached.current = false;
            }).catch(err => {
                // Connection failed silently
            });
        }

        return () => {
            if (signalRConnection) {
                signalRConnection.stop();
            }
            setConnection(null);
            isListenerAttached.current = false;
        };
    }, [signalRConnection]);
   
    useEffect(() => {
        if (connection) {
            let wasDisconnected = false;

            const handleStateChange = (state) => {
                if (wasDisconnected && state === 'Connected') {
                    if (onReconnect) {
                        onReconnect();
                    }
                    wasDisconnected = false;
                }
                
                previousConnectionState.current = state;
            };

            previousConnectionState.current = connection.state;
            
            connection.onclose(() => {
                wasDisconnected = true;
                handleStateChange('Disconnected');
            });
            
            const stateCheckInterval = setInterval(() => {
                if (connection.state !== previousConnectionState.current) {
                    handleStateChange(connection.state);
                }
            }, 1000);

            return () => {
                clearInterval(stateCheckInterval);
            };
        }
    }, [connection, onReconnect]);

    useEffect(() => {
        if (!connection) return;
        if (connection.state !== 'Connected') return;
        if (!requestBody || !lastProcessedTimestamp) return;
        if (isListenerAttached.current) return;

        connection.on('ChannelDataResponse', (data) => {
            onIncomingData(data)
        });
        isListenerAttached.current = true;

        connection.invoke('InitializeTimeBasedChannelDataForUnits', requestBody, lastProcessedTimestamp)
            .then(() => {
            })
            .catch(err => {
            });

        return () => {
            connection.off('ChannelDataResponse');
            isListenerAttached.current = false;
        };
    }, [connection, lastProcessedTimestamp, onIncomingData]);

    return null;
};

export default UnitBoxChartSignalR;
